import { NativeModules } from 'react-native';

const { RNZoomVideoSdkSessionStatisticsInfo } = NativeModules;

export type ZoomVideoSdkSessionStatisticsInfoType = {
  /**
    * Fetches audio statistics information related to the SDK session.
    */
  audioStatisticsInfo?: () => Promise<ZoomVideoSdkSessionAudioStatisticsInfoType>;

  /**
   * Fetches video statistics information related to the SDK session.
   */
  videoStatisticsInfo?: () => Promise<ZoomVideoSdkSessionVideoStatisticsInfoType>;

  /**
   * Fetches share statistics information related to the SDK session.
   */
  shareStatisticsInfo?: () => Promise<ZoomVideoSdkSessionShareStatisticsInfoType>;
};

export type ZoomVideoSdkSessionAudioStatisticsInfoType = {
  /**
   * Retrieves the frequency of received audio.
   */
  audioRecvFrequency?: () => Promise<number>;

  /**
   * Retrieves the jitter of received audio.
   */
  audioRecvJitter?: () => Promise<number>;

  /**
   * Retrieves the latency of received audio.
   */
  audioRecvLatency?: () => Promise<number>;

  /**
   * Retrieves the average packet loss of received audio.
   */
  audioRecvPacketLossAvg?: () => Promise<number>;

  /**
   * Retrieves the maximum packet loss of received audio.
   */
  audioRecvPacketLossMax?: () => Promise<number>;

  /**
   * Retrieves the frequency of sent audio.
   */
  audioSendFrequency?: () => Promise<number>;

  /**
   * Retrieves the jitter of sent audio.
   */
  audioSendJitter?: () => Promise<number>;

  /**
   * Retrieves the latency of sent audio.
   */
  audioSendLatency?: () => Promise<number>;

  /**
   * Retrieves the average packet loss of sent audio.
   */
  audioSendPacketLossAvg?: () => Promise<number>;

  /**
   * Retrieves the maximum packet loss of sent audio.
   */
  audioSendPacketLossMax?: () => Promise<number>;
};

export type ZoomVideoSdkSessionVideoStatisticsInfoType = {
  /**
   * Retrieves the frames per second of received video.
   */
  videoRecvFps?: () => Promise<number>;

  /**
   * Retrieves the frame height of received video.
   */
  videoRecvFrameHeight?: () => Promise<number>;

  /**
   * Retrieves the frame width of received video.
   */
  videoRecvFrameWidth?: () => Promise<number>;

  /**
   * Retrieves the jitter of received video.
   */
  videoRecvJitter?: () => Promise<number>;

  /**
   * Retrieves the latency of received video.
   */
  videoRecvLatency?: () => Promise<number>;

  /**
   * Retrieves the average packet loss of received video.
   */
  videoRecvPacketLossAvg?: () => Promise<number>;

  /**
   * Retrieves the maximum packet loss of received video.
   */
  videoRecvPacketLossMax?: () => Promise<number>;

  /**
   * Retrieves the frames per second of sent video.
   */
  videoSendFps?: () => Promise<number>;

  /**
   * Retrieves the frame height of sent video.
   */
  videoSendFrameHeight?: () => Promise<number>;

  /**
   * Retrieves the frame width of sent video.
   */
  videoSendFrameWidth?: () => Promise<number>;

  /**
   * Retrieves the jitter of sent video.
   */
  videoSendJitter?: () => Promise<number>;

  /**
   * Retrieves the latency of sent video.
   */
  videoSendLatency?: () => Promise<number>;

  /**
   * Retrieves the average packet loss of sent video.
   */
  videoSendPacketLossAvg?: () => Promise<number>;

  /**
   * Retrieves the maximum packet loss of sent video.
   */
  videoSendPacketLossMax?: () => Promise<number>;
};

export type ZoomVideoSdkSessionShareStatisticsInfoType = {
  /**
   * Retrieves the frames per second of received share content.
   */
  shareRecvFps?: () => Promise<number>;

  /**
   * Retrieves the frame height of received share content.
   */
  shareRecvFrameHeight?: () => Promise<number>;

  /**
   * Retrieves the frame width of received share content.
   */
  shareRecvFrameWidth?: () => Promise<number>;

  /**
   * Retrieves the jitter of received share content.
   */
  shareRecvJitter?: () => Promise<number>;

  /**
   * Retrieves the latency of received share content.
   */
  shareRecvLatency?: () => Promise<number>;

  /**
   * Retrieves the average packet loss of received share content.
   */
  shareRecvPacketLossAvg?: () => Promise<number>;

  /**
   * Retrieves the maximum packet loss of received share content.
   */
  shareRecvPacketLossMax?: () => Promise<number>;

  /**
   * Retrieves the frames per second of sent share content.
   */
  shareSendFps?: () => Promise<number>;

  /**
   * Retrieves the frame height of sent share content.
   */
  shareSendFrameHeight?: () => Promise<number>;

  /**
   * Retrieves the frame width of sent share content.
   */
  shareSendFrameWidth?: () => Promise<number>;

  /**
   * Retrieves the jitter of sent share content.
   */
  shareSendJitter?: () => Promise<number>;

  /**
   * Retrieves the latency of sent share content.
   */
  shareSendLatency?: () => Promise<number>;

  /**
   * Retrieves the average packet loss of sent share content.
   */
  shareSendPacketLossAvg?: () => Promise<number>;

  /**
   * Retrieves the maximum packet loss of sent share content.
   */
  shareSendPacketLossMax?: () => Promise<number>;
};

export class ZoomVideoSdkSessionStatisticsInfo
  implements
    ZoomVideoSdkSessionStatisticsInfoType,
    ZoomVideoSdkSessionAudioStatisticsInfoType,
    ZoomVideoSdkSessionVideoStatisticsInfoType,
    ZoomVideoSdkSessionShareStatisticsInfoType
{
  private static instance: ZoomVideoSdkSessionStatisticsInfo;

  constructor() {}

  static getInstance() {
    if (!this.instance) {
      this.instance = new this();
    }
    return this.instance;
  }

  async audioStatisticsInfo() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioStatisticsInfo();
  }

  async audioRecvFrequency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioRecvFrequency();
  }

  async audioRecvJitter() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioRecvJitter();
  }

  async audioRecvLatency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioRecvLatency();
  }

  async audioRecvPacketLossAvg() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioRecvPacketLossAvg();
  }

  async audioRecvPacketLossMax() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioRecvPacketLossMax();
  }

  async audioSendFrequency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioSendFrequency();
  }

  async audioSendJitter() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioSendJitter();
  }

  async audioSendLatency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioSendLatency();
  }

  async audioSendPacketLossAvg() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioSendPacketLossAvg();
  }

  async audioSendPacketLossMax() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getAudioSendPacketLossMax();
  }

  async videoStatisticsInfo() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoStatisticsInfo();
  }

  async videoRecvFps() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvFps();
  }

  async videoRecvFrameHeight() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvFrameHeight();
  }

  async videoRecvFrameWidth() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvFrameWidth();
  }

  async videoRecvJitter() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvJitter();
  }

  async videoRecvLatency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvLatency();
  }

  async videoRecvPacketLossAvg() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvPacketLossAvg();
  }

  async videoRecvPacketLossMax() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoRecvPacketLossMax();
  }

  async videoSendFps() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendFps();
  }

  async videoSendFrameHeight() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendFrameHeight();
  }

  async videoSendFrameWidth() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendFrameWidth();
  }

  async videoSendJitter() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendJitter();
  }

  async videoSendLatency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendLatency();
  }

  async videoSendPacketLossAvg() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendPacketLossAvg();
  }

  async videoSendPacketLossMax() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getVideoSendPacketLossMax();
  }

  async shareStatisticsInfo() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareStatisticsInfo();
  }

  async shareRecvFps() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvFps();
  }

  async shareRecvFrameHeight() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvFrameHeight();
  }

  async shareRecvFrameWidth() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvFrameWidth();
  }

  async shareRecvJitter() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvJitter();
  }

  async shareRecvLatency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvLatency();
  }

  async shareRecvPacketLossAvg() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvPacketLossAvg();
  }

  async shareRecvPacketLossMax() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareRecvPacketLossMax();
  }

  async shareSendFps() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendFps();
  }

  async shareSendFrameHeight() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendFrameHeight();
  }

  async shareSendFrameWidth() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendFrameWidth();
  }

  async shareSendJitter() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendJitter();
  }

  async shareSendLatency() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendLatency();
  }

  async shareSendPacketLossAvg() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendPacketLossAvg();
  }

  async shareSendPacketLossMax() {
    return await RNZoomVideoSdkSessionStatisticsInfo.getShareSendPacketLossMax();
  }
}
